import { ORPCError } from "@orpc/client";
import {
  DELETION_GRACE_DAYS,
  DELETION_SIGN_IN_WINDOW_MINUTES,
} from "@pax/api-contract/accounts/v1alpha1";
import type { SocialAuthProvider } from "@pax/api-contract/client-config";
import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Skeleton,
} from "@pax/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { createContext, type ReactNode, use, useEffect, useState } from "react";
import { formatCredits } from "@/features/credits/format";
import { creditBalanceQuery } from "@/features/credits/queries";
import { api, orpc, reachable, requestErrorMessage, UNREACHABLE_MESSAGE } from "@/lib/api";
import { signInErrorCallback } from "./auth-urls";
import { SocialAuthButtons } from "./components/SocialAuthButtons";
import {
  clientConfigQuery,
  type Session,
  sessionKey,
  signOut,
  useCurrentProfile,
  useSession,
} from "./queries";
import { linkedSignInMethodsQuery, PROVIDER_NAMES } from "./sign-in-methods";
import { signInWithProvider } from "./social-sign-in";

const MINUTE_MS = 60 * 1000;
const DAY_MS = 24 * 60 * MINUTE_MS;
/** The countdown page a pending deletion locks the app to. */
export const ACCOUNT_DELETION_PATH = "/account/deletion";
/** Where a deleted account's owner lands, after deleting it now or trying to sign in to it again. */
export const ACCOUNT_DELETED_PATH = "/account/deleted";
/**
 * Marks where a sign-in started from deletion comes back to: `schedule` opens the dialog again, and
 * `now` opens the countdown page's delete-now confirmation.
 */
export const RESUME_PARAM = "accountDeletion";

export const destructiveSolid = "bg-destructive text-white hover:bg-destructive/90";

interface AccountDeletionContextValue {
  openAccountDeletion: () => void;
}

const AccountDeletionContext = createContext<AccountDeletionContextValue | null>(null);

export function useAccountDeletion(): AccountDeletionContextValue {
  const value = use(AccountDeletionContext);
  if (!value) throw new Error("useAccountDeletion must be used inside AccountDeletionProvider");
  return value;
}

/**
 * The signed-in account's pending deletion, or null. Staff acting as a player cannot manage it.
 * `watch` refetches every few seconds, to see the purge start.
 */
export function useAccountDeletionStatus(
  session: Session | null | undefined,
  { watch = false }: { watch?: boolean } = {},
) {
  return useQuery({
    ...orpc.accounts.deletion.get.queryOptions({ select: response => response.deletion }),
    enabled: canDelete(session),
    refetchInterval: watch ? 5000 : false,
  });
}

function canDelete(session: Session | null | undefined): session is Session {
  return Boolean(session && !session.session.impersonatedBy);
}

/**
 * Whether the signed-in account is waiting to be deleted, which locks the app to the deletion
 * page. The session carries the purge time on the user, so this needs no request of its own. Staff
 * acting as the player aren't locked.
 */
export function isDeletionPending(session: Session | null | undefined): boolean {
  return Boolean(session?.user.deletionPurgeAfter && !session.session.impersonatedBy);
}

/**
 * Deleting your account, from the account panel. Confirming schedules the deletion, and the app
 * then shows only its countdown, where you can keep the account or delete it now.
 */
export function AccountDeletionProvider({ children }: { children: ReactNode }) {
  const { data: session } = useSession();
  const href = useLocation({ select: location => location.href });
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const resuming =
    new URL(href, window.location.origin).searchParams.get(RESUME_PARAM) === "schedule";
  useEffect(() => {
    if (!resuming || !canDelete(session)) return;
    setOpen(true);
    void navigate({ href: withoutResume(href), replace: true });
  }, [href, navigate, resuming, session]);

  return (
    <AccountDeletionContext value={{ openAccountDeletion: () => setOpen(true) }}>
      {children}
      <Dialog open={open} onOpenChange={setOpen}>
        {/* Mounted with the dialog, so every opening starts from the first step. */}
        {open && canDelete(session) ? (
          <DeleteAccountSteps session={session} onDone={() => setOpen(false)} />
        ) : null}
      </Dialog>
    </AccountDeletionContext>
  );
}

interface AccountThing {
  label: string;
  /** What the wide panel shows beside it, such as a username or a count. */
  detail?: string;
  /** A number the narrow layout keeps when it drops the other details. */
  count?: string;
}

/** What deleting removes and keeps, then one way forward: schedule it. */
function DeleteAccountSteps({ session, onDone }: { session: Session; onDone: () => void }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [signInAgain, setSignInAgain] = useState(false);
  const profile = useCurrentProfile(session.user).data;
  const { data: credits } = useQuery(creditBalanceQuery(session.user.id));
  const { data: providers } = useQuery({
    ...linkedSignInMethodsQuery,
    enabled: !session.user.isAnonymous,
  });

  const schedule = useMutation({
    mutationFn: () =>
      reachable(api.accounts.deletion.create({ body: { when: "after_grace_period" } })),
    onSuccess: async deletion => {
      // The page and the lock read these two, so both are current before the dialog goes.
      queryClient.setQueryData(orpc.accounts.deletion.get.queryKey(), { deletion });
      await queryClient.invalidateQueries({ queryKey: sessionKey });
      onDone();
      await navigate({ to: ACCOUNT_DELETION_PATH });
    },
    onError: error => {
      if (error instanceof ORPCError && error.code === "SIGN_IN_AGAIN") setSignInAgain(true);
    },
  });

  if (signInAgain) {
    return (
      <DialogContent className="max-w-md gap-0 p-6">
        <SignInAgain resume="schedule" onBack={() => setSignInAgain(false)} />
      </DialogContent>
    );
  }

  const pictures = profile
    ? String(Number(Boolean(profile.avatar)) + Number(Boolean(profile.banner)))
    : undefined;
  const deleted: AccountThing[] = session.user.isAnonymous
    ? [
        { label: "Guest account" },
        {
          label: "Pax credits",
          detail: credits === undefined ? undefined : formatCredits(credits),
          count: credits === undefined ? undefined : formatCredits(credits),
        },
      ]
    : [
        {
          label: "Name and username",
          detail: profile
            ? profile.username
              ? `@${profile.username}`
              : profile.displayName
            : undefined,
        },
        { label: "Profile pictures", detail: pictures, count: pictures },
        {
          label: "Friends",
          detail: profile?.friendCount.toLocaleString(),
          count: profile?.friendCount.toLocaleString(),
        },
        {
          label: "Credits",
          detail: credits === undefined ? undefined : formatCredits(credits),
          count: credits === undefined ? undefined : formatCredits(credits),
        },
        {
          label: "Sign-in methods",
          detail: providers?.map(provider => PROVIDER_NAMES[provider]).join(", "),
        },
      ];
  const unlinked: AccountThing[] = [
    { label: "Presets and agents" },
    { label: "Games" },
    { label: "Assets" },
    { label: "Comments" },
  ];
  const lead = `On ${formatDay(Date.now() + DELETION_GRACE_DAYS * DAY_MS)} we delete your account. You can cancel until then.${session.user.isAnonymous ? " Keep this browser session to cancel; a guest account has no sign-in method to return with." : ""}`;
  const finish = (
    <>
      {schedule.error && !signInAgain ? (
        <ErrorMessage className="mt-4" message={requestErrorMessage(schedule.error)} />
      ) : null}
      <Button
        size="lg"
        className="mt-5 w-full"
        disabled={schedule.isPending}
        onClick={() => (canAuthorizeDeletion(session) ? schedule.mutate() : setSignInAgain(true))}
      >
        Delete account
      </Button>
    </>
  );

  // Two columns when the dialog has room: the words, then the account in two lists. Narrower, as
  // on a phone, the lists move under the words, side by side, keeping only their counts.
  return (
    <DialogContent className="max-w-2xl gap-0 overflow-hidden p-0">
      <div className="@container">
        <div className="hidden grid-cols-[1fr_16.5rem] @[34rem]:grid">
          <div className="flex flex-col p-6">
            <DialogTitle className="pr-6 text-lg leading-tight">Delete your account?</DialogTitle>
            <DialogDescription className="mt-2 text-pretty">{lead}</DialogDescription>
            <FinePrint className="mt-auto pt-5" />
            {finish}
          </div>
          <div className="bg-foreground/[0.04] p-5 pt-6">
            <ThingHeading>Deleted</ThingHeading>
            <Things things={deleted} show="detail" />
            <div className="my-4 h-px bg-foreground/10" />
            <ThingHeading>Kept, but unlinked from you</ThingHeading>
            <Things things={unlinked} show="detail" />
            <p className="mt-4 text-xs text-muted-foreground">Shown as “Deleted account”.</p>
          </div>
        </div>

        <div className="p-6 @[34rem]:hidden">
          <DialogTitle className="pr-6 text-lg leading-tight">Delete your account?</DialogTitle>
          <DialogDescription className="mt-2 text-pretty">{lead}</DialogDescription>
          <div className="mt-5 grid grid-cols-2 gap-4 rounded-xl bg-foreground/[0.04] p-4">
            <div className="min-w-0">
              <ThingHeading>Deleted</ThingHeading>
              <Things things={deleted} show="count" />
            </div>
            <div className="min-w-0">
              <ThingHeading>Unlinked from you</ThingHeading>
              <Things things={unlinked} show="count" />
            </div>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Unlinked things stay on Pax as “Deleted account”.
          </p>
          <FinePrint className="mt-4" />
          {finish}
        </div>
      </div>
    </DialogContent>
  );
}

function ThingHeading({ children }: { children: ReactNode }) {
  return <p className="text-xs font-medium text-muted-foreground">{children}</p>;
}

function Things({ things, show }: { things: AccountThing[]; show: "detail" | "count" }) {
  return (
    <ul className="mt-1.5 flex flex-col gap-1.5 text-xs">
      {things.map(thing => (
        <li key={thing.label} className="flex items-baseline gap-2">
          <span className="min-w-0 flex-1 truncate">{thing.label}</span>
          <span className="text-muted-foreground tabular-nums">{thing[show]}</span>
        </li>
      ))}
    </ul>
  );
}

/** A day such as "November 26", with the year only when it isn't this one. */
/** A day, with the year only when it isn't this one, such as "November 26". */
function formatDay(time: number | string): string {
  const day = new Date(time);
  return day.toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    ...(day.getFullYear() === new Date().getFullYear() ? {} : { year: "numeric" }),
  });
}

/** A day and time, such as "November 26 at 3:04 PM". */
export function formatMoment(time: number | string): string {
  const clock = new Date(time).toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${formatDay(time)} at ${clock}`;
}

function FinePrint({ className }: { className?: string }) {
  return (
    <p className={cn("text-xs text-pretty text-muted-foreground", className)}>
      Pax keeps some records for safety and legal reasons. An App Store subscription isn't ended by
      Pax, so cancel it in Settings first.
    </p>
  );
}

/**
 * Deleting needs a recent sign-in. The account's own providers sign in again in one step and come
 * back to the dialog. An account with none to offer, such as one signed in with a link, signs out
 * and signs in on the sign-in page, which brings it back here too.
 */
export function SignInAgain({
  resume,
  onBack,
}: {
  resume: "schedule" | "now";
  onBack: () => void;
}) {
  const href = useLocation({ select: location => location.href });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const methods = useQuery(clientConfigQuery);
  const linked = useQuery(linkedSignInMethodsQuery);
  const [error, setError] = useState<string | null>(null);
  const [leaving, setLeaving] = useState(false);
  const providers = (methods.data?.socialProviders ?? []).filter(provider =>
    linked.data?.includes(provider),
  );
  const loading = methods.isPending || linked.isPending;
  const returnTo = withResume(href, resume);

  async function signInWith(provider: SocialAuthProvider) {
    setError(null);
    try {
      await signInWithProvider({
        provider,
        callbackURL: returnTo,
        errorCallbackURL: signInErrorCallback(href),
      });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : UNREACHABLE_MESSAGE);
    }
  }

  async function signInOnSignInPage() {
    setLeaving(true);
    try {
      await signOut(queryClient);
    } finally {
      await navigate({ to: "/login", search: { next: returnTo } });
    }
  }

  return (
    <>
      <DialogTitle className="text-lg">Sign in again to continue</DialogTitle>
      <DialogDescription className="mt-1.5 text-pretty">
        To keep your account safe, deleting it needs a sign-in from the last{" "}
        {DELETION_SIGN_IN_WINDOW_MINUTES} minutes. You'll come back here afterwards.
      </DialogDescription>
      <ErrorMessage className="mt-4" message={error} />
      <div className="mt-5">
        {loading ? (
          <Skeleton className="h-12 w-full rounded-full" />
        ) : providers.length > 0 ? (
          <SocialAuthButtons
            providers={providers}
            onSelect={provider => void signInWith(provider)}
          />
        ) : (
          <Button
            size="lg"
            className="w-full"
            disabled={leaving}
            onClick={() => void signInOnSignInPage()}
          >
            Sign in again
          </Button>
        )}
      </div>
      <Button variant="ghost" className="mt-3 w-full" onClick={onBack}>
        Back
      </Button>
    </>
  );
}

export function ErrorMessage({
  message,
  className,
}: {
  message: string | null;
  className?: string;
}) {
  if (!message) return null;
  return (
    <p
      role="alert"
      className={cn("rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive", className)}
    >
      {message}
    </p>
  );
}

/** Guests use their only session; connected accounts need a recent sign-in. */
export function canAuthorizeDeletion(session: Session): boolean {
  const signedInAt = new Date(session.session.createdAt).getTime();
  return (
    session.user.isAnonymous === true ||
    Date.now() - signedInAt < DELETION_SIGN_IN_WINDOW_MINUTES * MINUTE_MS
  );
}

/** The address to come back to after signing in, marked to pick up the step it left. */
function withResume(href: string, resume: "schedule" | "now"): string {
  const target = new URL(href, window.location.origin);
  target.searchParams.set(RESUME_PARAM, resume);
  return `${target.pathname}${target.search}${target.hash}`;
}

export function withoutResume(href: string): string {
  const target = new URL(href, window.location.origin);
  target.searchParams.delete(RESUME_PARAM);
  return `${target.pathname}${target.search}${target.hash}`;
}
